import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import type { Color } from "@salestrak/ui";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useActivityGroupCounts } from "../shared/use-activity-group-counts";
import type { Omittable } from "@salestrak/ui";

export type StageMovesMode = "byStage" | "total";

export type StageMoveSlice = {
  stageId: string;
  label: string;
  /** The stage's own palette color — charts wear it. */
  color: Color | null;
  count: number;
};

/**
 * Exact per-stage transition counts from the server aggregation: the
 * `"stage.entered"` grouping token counts stage-transition activities INTO each
 * stage over the scoped window (the token itself filters to stage transitions).
 */
export function useStageMoves(
  mode: StageMovesMode,
  limit: number,
  // The result relaxes: `error` is Apollo's own `error` field, which is
  // `undefined` while the query is healthy, and the widget body reads it as a
  // truthiness check.
): Omittable<{
  total: number;
  slices: StageMoveSlice[];
  loading: boolean;
  error?: ErrorLike;
}> {
  const { stageMap } = useWorkspaceData();
  const { where } = useScopedActivityWhere(undefined, {});
  const { counts, ready, error } = useActivityGroupCounts({ groupBy: "stage.entered", where });

  const slices = useMemo(() => {
    if (mode === "total") return [];
    return Object.entries(counts)
      .map(([stageId, count]) => {
        const stage = stageMap.get(stageId);
        return {
          stageId,
          label: stage?.name ?? "Unknown stage",
          color: (stage?.icon?.color as Color) ?? null,
          count,
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }, [counts, limit, mode, stageMap]);

  const total = useMemo(
    () => Object.values(counts).reduce((sum, count) => sum + count, 0),
    [counts],
  );

  return { total, slices, loading: !ready, error };
}
